import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Briefcase,
  FileText,
  Target,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Sparkles,
  ArrowRight,
  TrendingUp,
  Bell,
  Building2,
  Loader2,
  CreditCard,
  Crown,
  LogOut
} from 'lucide-react';

export default function JobSeekerDashboard() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, appsRes] = await Promise.all([
        api.get('/stats/job-seeker'),
        api.get('/applications/my')
      ]);
      setStats(statsRes.data);
      setApplications(appsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      const response = await api.get('/ai/recommendations');
      setRecommendations(response.data);
    } catch (error) {
      toast.error('Failed to fetch recommendations');
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      reviewed: 'bg-blue-100 text-blue-700',
      shortlisted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      hired: 'bg-purple-100 text-purple-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      reviewed: Eye,
      shortlisted: Target,
      rejected: XCircle,
      hired: CheckCircle2
    };
    const Icon = icons[status] || Clock;
    return <Icon className="w-3 h-3 mr-1" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="job-seeker-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Welcome back, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground mt-1">
              Here's an overview of your job search progress
            </p>
          </div>
          <Button variant="outline" className="border-gray-300" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Applications</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {stats?.total_applications || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#003366]/10 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-[#003366]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Shortlisted</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">
                    {stats?.applications_by_status?.shortlisted || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Target className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Under Review</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">
                    {(stats?.applications_by_status?.pending || 0) + (stats?.applications_by_status?.reviewed || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Profile Completion</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">
                    {stats?.profile_completion || 0}%
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <Progress value={stats?.profile_completion || 0} className="mt-3 h-2" />
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Applications */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Recent Applications</CardTitle>
                  <Link to="/applications">
                    <Button variant="ghost" size="sm">
                      View All
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {applications.length > 0 ? (
                  <div className="space-y-4">
                    {applications.slice(0, 5).map((app) => (
                      <div 
                        key={app.id} 
                        className="flex items-center justify-between p-4 rounded-lg border hover:border-[#003366]/30 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#F0F4F8] flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-[#003366]" />
                          </div>
                          <div>
                            <Link 
                              to={`/jobs/${app.job_id}`}
                              className="font-medium hover:text-[#003366] transition-colors"
                            >
                              {app.job?.title || 'Job Title'}
                            </Link>
                            <p className="text-sm text-muted-foreground">
                              {app.job?.company_name || 'Company'}
                            </p>
                          </div>
                        </div>
                        <Badge className={getStatusColor(app.status)}>
                          {getStatusIcon(app.status)}
                          {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No applications yet</p>
                    <Link to="/jobs">
                      <Button data-testid="browse-jobs-btn">Browse Jobs</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* AI Recommendations */}
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  AI Job Matches
                </CardTitle>
                <CardDescription>
                  Jobs that match your profile
                </CardDescription>
              </CardHeader>
              <CardContent>
                {recommendations.length > 0 ? (
                  <div className="space-y-3">
                    {recommendations.slice(0, 3).map((rec) => (
                      <Link
                        key={rec.job_id}
                        to={`/jobs/${rec.job_id}`}
                        className="block p-3 bg-white rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm line-clamp-1">{rec.title}</span>
                          <Badge className="gradient-ai text-white text-xs">
                            {Math.round(rec.match_score)}%
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{rec.company_name} · {rec.location}</p>
                        {rec.reasons?.length > 0 && (
                          <ul className="space-y-0.5">
                            {rec.reasons.slice(0, 2).map((r, i) => (
                              <li key={i} className="text-xs text-purple-700 flex items-start gap-1">
                                <span className="mt-0.5 shrink-0">•</span><span>{r}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Button 
                    onClick={fetchRecommendations}
                    disabled={loadingRecommendations}
                    className="w-full"
                    variant="outline"
                    data-testid="get-recommendations-btn"
                  >
                    {loadingRecommendations ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                    )}
                    Get Recommendations
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Subscription Status */}
            <Card className="border-[#003366]/20 bg-gradient-to-br from-[#003366]/5 to-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Crown className="w-5 h-5 text-[#003366]" />
                  Plan & Subscription
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Current Plan</p>
                  <p className="font-semibold text-lg text-[#003366]">
                    {user?.subscription?.plan_slug ? user.subscription.plan_slug.charAt(0).toUpperCase() + user.subscription.plan_slug.slice(1) : 'Free'}
                  </p>
                </div>
                {user?.subscription?.expires_at && (
                  <div className="space-y-2 pb-2 border-b">
                    <p className="text-sm text-muted-foreground">Expires</p>
                    <p className="font-medium text-sm">
                      {new Date(user.subscription.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                <Link to="/pricing" className="block">
                  <Button className="w-full bg-[#003366] hover:bg-[#003366]/90">
                    <CreditCard className="w-4 h-4 mr-2" />
                    {user?.subscription?.plan_slug ? 'Upgrade Plan' : 'Choose Plan'}
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/profile" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="w-4 h-4 mr-2" />
                    Update Profile
                  </Button>
                </Link>
                <Link to="/cv-builder" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="w-4 h-4 mr-2" />
                    CV Builder
                  </Button>
                </Link>
                <Link to="/alerts" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Bell className="w-4 h-4 mr-2" />
                    Manage Job Alerts
                  </Button>
                </Link>
                <Link to="/jobs" className="block">
                  <Button className="w-full bg-[#E11D48] hover:bg-[#E11D48]/90" data-testid="search-jobs-btn">
                    Search Jobs
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
